import { useId, useState } from 'react'
import type { ElectricSpecs } from '../api/types'
import { formatBatteryWarranty, formatDriveType } from '../domain/electric'
import { formatList, formatNumber } from '../domain/format'
import { rows } from '../domain/rows'
import { DataRows } from './Section'

/**
 * Battery, range and charging figures for a fully electric car, with the credit its licence
 * requires.
 *
 * Three things here are obligations rather than styling:
 *
 * - **The source note is not optional.** These figures are published under CC BY-SA 4.0, and
 *   attribution under that licence is *unconditional* — it attaches to any sharing, not only to
 *   adapted data. Deleting `SourceNote`, or rendering the figures on a page that does not reach it,
 *   is a licence breach rather than a missing nicety. `source`, `sourceUrl`, `licence` and
 *   `licenceUrl` are `required` on the API's record so this cannot quietly be written any other
 *   way.
 * - **The matched variant is stated in the subheading, always.** The catalogue is organised by
 *   variant and a Dutch registration names none, so the backend infers it — strictly, refusing
 *   anything ambiguous, but still inferring. Naming the variant is what lets a reader notice when
 *   the inference is wrong. It is set in `electricSectionSubtitle`, so this component does not
 *   repeat it.
 * - **This describes the model, not this car.** Two cars of one variant get identical figures, and
 *   a battery that has aged eight years is not the battery in this table. The line at the top says
 *   so, for the same reason the review section carries its own scope note.
 *
 * The WLTP range and the power deliberately do not appear here: the RDW publishes both for this
 * exact vehicle and they are already in the powertrain card. Repeating a model-level figure beside
 * a car-level one invites the reader to trust the wrong number.
 */
export function ElectricSection({ electric }: { electric: ElectricSpecs }) {
  const items = rows(
    { label: 'Accucapaciteit (bruto)', value: formatNumber(electric.batteryCapacityKwh, 'kWh') },
    { label: 'Bruikbare accucapaciteit', value: formatNumber(electric.batteryUsableKwh, 'kWh') },
    { label: 'Accuchemie', value: electric.batteryChemistry },
    {
      label: 'Accugarantie',
      value: formatBatteryWarranty(
        electric.batteryWarrantyYears,
        electric.batteryWarrantyKm,
        (km) => formatNumber(km, 'km') ?? '',
      ),
    },
    { label: 'Actieradius in de praktijk', value: formatNumber(electric.rangeRealWorldKm, 'km') },
    {
      label: 'Verbruik in de praktijk',
      value: formatNumber(electric.consumptionRealWorldKwhPer100Km, 'kWh/100 km'),
    },
    { label: 'Laadvermogen AC', value: formatAcPower(electric) },
    { label: 'Laadvermogen DC (snelladen)', value: formatNumber(electric.dcChargePowerKw, 'kW') },
    { label: 'Laadtijd 10–80% (DC)', value: formatNumber(electric.dcChargeTime10To80Min, 'min') },
    { label: 'Laadaansluitingen', value: formatList(electric.connectorTypes) },
    { label: 'Aantal elektromotoren', value: formatNumber(electric.motorCount) },
    { label: 'Aandrijving', value: formatDriveType(electric.driveType) },
    { label: 'Bagageruimte voorin (frunk)', value: formatFrunk(electric.frunkCapacityLitres) },
  )

  return (
    <div className="electric">
      <p className="electric__scope">
        Deze gegevens beschrijven het model, niet deze specifieke auto. De accu van een gebruikte
        auto kan door ouderdom en gebruik minder capaciteit hebben dan hieronder staat.
      </p>

      <DataRows items={items} />

      <SourceNote electric={electric} />
    </div>
  )
}

/**
 * The credit, collapsed to one quiet line.
 *
 * A reader looking at a battery table does not need a licence lecture, so the resting state is
 * "Bron: EVDB" in the muted footnote style and nothing else. CC BY-SA 4.0 §3(a)(2) allows the
 * required notices to be given "in any reasonable manner based on the medium, means and context",
 * and explicitly accepts satisfying them with a link to a resource that carries the information —
 * so a disclosure one click away, on the same page, is comfortably inside what the licence asks
 * for. It is *not* licence to drop the notices altogether.
 *
 * Built from a button and a `hidden` div rather than `<details>` on purpose. A closed `<details>`
 * does not print its contents in any reliable way, and this is exactly the content that has to
 * survive printing — the print stylesheet overrides `hidden` here and appends each address after
 * its link text, because on paper a link cannot be followed and the credit still has to stand up.
 */
function SourceNote({ electric }: { electric: ElectricSpecs }) {
  const [expanded, setExpanded] = useState(false)
  const bodyId = useId()

  return (
    <div className="source-note">
      <button
        type="button"
        className="source-note__toggle"
        aria-expanded={expanded}
        aria-controls={bodyId}
        onClick={() => setExpanded((open) => !open)}
      >
        Bron: {electric.source}
      </button>

      <div className="source-note__body" id={bodyId} hidden={!expanded}>
        <p>
          De accu- en laadgegevens hierboven komen van{' '}
          <a
            className="source-note__link"
            href={electric.sourceUrl}
            target="_blank"
            // nofollow because this is a required credit, not rank we are choosing to pass on.
            rel="nofollow noreferrer noopener"
          >
            {electric.source}
          </a>{' '}
          en zijn gepubliceerd onder de licentie{' '}
          <a
            className="source-note__link"
            href={electric.licenceUrl}
            target="_blank"
            rel="nofollow noreferrer noopener"
          >
            {electric.licence}
          </a>
          . AutoChecken neemt ze ongewijzigd over.
        </p>
      </div>
    </div>
  )
}

/**
 * AC charge power, with the phase count where the catalogue has it.
 *
 * The two belong together: 11 kW over three phases and 7,4 kW over one are the difference between
 * a charger that works at home and one that does not, and the number alone does not say which.
 */
function formatAcPower(electric: ElectricSpecs): string | undefined {
  const power = formatNumber(electric.acChargePowerKw, 'kW')

  if (!power) {
    return undefined
  }

  return electric.acChargePhases ? `${power} (${electric.acChargePhases}-fase)` : power
}

/**
 * Frunk capacity, where `0` is a fact rather than a gap.
 *
 * The catalogue publishes zero for a model that has no storage under the bonnet, which is worth
 * knowing on a car people expect one on. Printing "0 l" reads as a rendering fault, so the zero is
 * stated in words — and an absent value still drops the row entirely, as everywhere else.
 */
function formatFrunk(litres: number | undefined): string | undefined {
  if (litres === undefined) {
    return undefined
  }

  return litres === 0 ? 'Niet aanwezig' : formatNumber(litres, 'l')
}
